import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Meeting {
  id: string;
  title: string;
  description: string;
  scheduledAt: string;
  duration: number;
  location: string;
  meetingType: string;
  meetingUrl?: string;
  status: string;
}

interface ChamaMember {
  id: string;
  user_id: string;
  name?: string;
  user_name?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface AttendanceRecord {
  userId: string;
  userName?: string;
  isPresent: boolean;
  attendanceType: string;
  markedAt?: string;
  joinTime?: string;
  leaveTime?: string;
}

const OnlineMeetingAttendance: React.FC = () => {
  const { chamaId, meetingId } = useParams<{ chamaId: string; meetingId: string }>();
  const navigate = useNavigate();
  const colors = getThemeColors('dark');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [chamaMembers, setChamaMembers] = useState<ChamaMember[]>([]);
  const [attendance, setAttendance] = useState<{[userId: string]: boolean}>({});
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [meetingActive, setMeetingActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [jitsiUrl, setJitsiUrl] = useState<string | null>(null);
  const hasJoinedRef = useRef(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    if (meetingId && chamaId) {
      loadAllData();
    }
  }, [meetingId, chamaId]);

  const loadAllData = async () => {
    setLoading(true);
    setLoadingProgress(0);

    try {
      // Load data in parallel for better performance
      const promises = [
        loadMeetingData(),
        loadChamaMembers(),
        loadExistingAttendance(),
        initializeJitsiMeeting(),
      ];

      // Update progress as each promise completes
      let completed = 0;
      const updateProgress = () => {
        completed++;
        setLoadingProgress((completed / promises.length) * 100);
      };

      // Wait for all promises to complete with timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Loading timeout after 30 seconds')), 30000);
      });

      await Promise.race([
        Promise.all(promises.map(p => p.then(updateProgress).catch(err => {
          console.error('Promise failed:', err);
          updateProgress(); // Still count as completed even if failed
          return null;
        }))),
        timeoutPromise
      ]);

      setDataLoaded(true);
    } catch (error) {
      console.error('❌ Failed to load data:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to load meeting data');
    } finally {
      setLoading(false);
    }
  };

  const loadMeetingData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) throw new Error('No auth token');

      const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load meeting: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setMeeting(data.data);
        // Check if meeting is currently active
        const now = new Date();
        const meetingStart = new Date(data.data.scheduledAt);
        const meetingEnd = new Date(meetingStart.getTime() + (data.data.duration * 60 * 1000));
        setMeetingActive(now >= meetingStart && now <= meetingEnd && data.data.status === 'ongoing');
      } else {
        throw new Error('Invalid meeting data received');
      }
    } catch (error) {
      console.error('❌ Failed to load meeting data:', error);
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const initializeJitsiMeeting = async () => {
    try {
      setIsConnecting(true);
      setConnectionError(null);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) throw new Error('No auth token for Jitsi');

      // Get Jitsi meeting connection data
      const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/join-jitsi`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userRole: 'member', // Default role
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get Jitsi connection: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Build Jitsi URL
        const baseUrl = 'https://8x8.vc';
        const roomName = `vpaas-magic-cookie-cb055ffbd0604dedb00b5a2540349c3c/${data.data.roomName}`;
        const encodedRoomName = encodeURIComponent(roomName);

        const params = new URLSearchParams();
        params.append('userInfo.displayName', 'VaultKe User');
        params.append('config.defaultLocalDisplayName', 'VaultKe User');
        params.append('config.defaultRemoteDisplayName', 'VaultKe User');
        params.append('interfaceConfig.APP_NAME', 'VaultKe');
        params.append('interfaceConfig.NATIVE_APP_NAME', 'VaultKe');
        params.append('interfaceConfig.SHOW_JITSI_WATERMARK', 'false');
        params.append('interfaceConfig.SHOW_WATERMARK_FOR_GUESTS', 'false');
        params.append('interfaceConfig.SHOW_BRAND_WATERMARK', 'false');
        params.append('interfaceConfig.SHOW_POWERED_BY', 'false');

        const fullUrl = `${baseUrl}/${encodedRoomName}?${params.toString()}`;
        setJitsiUrl(fullUrl);
        setIsConnected(true);
      } else {
        throw new Error('Invalid Jitsi connection data');
      }
    } catch (error) {
      console.error('❌ Failed to initialize Jitsi meeting:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to connect to meeting');
      throw error; // Re-throw to be caught by Promise.all
    } finally {
      setIsConnecting(false);
    }
  };

  const loadChamaMembers = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) throw new Error('No auth token for members');

      const response = await fetch(API_ENDPOINTS.CHAMA_MEMBERS(chamaId!), {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load members: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        setChamaMembers(data.data);
      } else {
        throw new Error('Invalid members data received');
      }
    } catch (error) {
      console.error('❌ Failed to load chama members:', error);
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const loadExistingAttendance = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) throw new Error('No auth token for attendance');

      const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/attendance`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load attendance: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        setAttendanceRecords(data.data);
        // Convert to attendance map
        const attendanceMap: {[userId: string]: boolean} = {};
        data.data.forEach((record: AttendanceRecord) => {
          attendanceMap[record.userId] = record.isPresent;
        });
        setAttendance(attendanceMap);
      } else {
        throw new Error('Invalid attendance data received');
      }
    } catch (error) {
      console.error('❌ Failed to load existing attendance:', error);
      throw error; // Re-throw to be caught by Promise.all
    }
  };

  const getMemberName = (member: ChamaMember) => {
    return member.name ||
           member.user_name ||
           member.username ||
           `${member.first_name || ''} ${member.last_name || ''}`.trim() ||
           member.email?.split('@')[0] ||
           `Member ${member.id.slice(-4)}`;
  };

  const toggleAttendance = (userId: string) => {
    setAttendance(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const markAllPresent = () => {
    const newAttendance: {[userId: string]: boolean} = {};
    chamaMembers.forEach(member => {
      const userId = member.user_id || member.id;
      newAttendance[userId] = true;
    });
    setAttendance(newAttendance);
  };

  const markAllAbsent = () => {
    const newAttendance: {[userId: string]: boolean} = {};
    chamaMembers.forEach(member => {
      const userId = member.user_id || member.id;
      newAttendance[userId] = false;
    });
    setAttendance(newAttendance);
  };

  const saveAttendance = async () => {
    try {
      setSaving(true);
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      // Prepare attendance data
      const attendanceData = chamaMembers.map(member => ({
        userId: member.user_id || member.id,
        userName: getMemberName(member),
        isPresent: attendance[member.user_id || member.id] || false,
        attendanceType: 'virtual',
        joinTime: attendance[member.user_id || member.id] ? new Date().toISOString() : undefined,
      }));

      const response = await fetch(`${API_ENDPOINTS.MEETINGS}${meetingId}/attendance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attendance: attendanceData
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Attendance saved successfully!');
          navigate(`/dashboard/chama/${chamaId}/meetings/${meetingId}/summary`);
        } else {
          alert(`Failed to save attendance: ${data.error || 'Unknown error'}`);
        }
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        alert(`Failed to save attendance: ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Failed to save attendance:', error);
      alert('Failed to save attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getAttendanceStats = () => {
    const present = Object.values(attendance).filter(Boolean).length;
    const total = chamaMembers.length;
    const absent = total - present;
    return { present, absent, total };
  };

  if (loading || isConnecting) {
    return (
      <div style={styles.container}>
        <UserDashboardHeader
          title="Online Meeting Attendance"
          showBackButton={true}
          showHomeButton={false}
        />
        <div style={styles.scrollableContent}>
          <div style={styles.content}>
            <div style={styles.loadingContainer}>
              <div style={{ fontSize: '48px', marginBottom: spacing.md }}>🎬</div>
              <div style={{ ...styles.loadingText, color: colors.text }}>
                {isConnecting ? 'Connecting to meeting...' : 'Loading meeting attendance...'}
              </div>
              <div style={{ ...styles.loadingProgress, backgroundColor: colors.background }}>
                <div
                  style={{
                    ...styles.loadingProgressBar,
                    backgroundColor: colors.primary,
                    width: `${loadingProgress}%`
                  }}
                />
              </div>
              <div style={{ ...styles.loadingSubtext, color: colors.textSecondary }}>
                {Math.round(loadingProgress)}% complete
              </div>
              {meeting && (
                <div style={{ ...styles.loadingSubtext, color: colors.textSecondary, marginTop: spacing.xs }}>
                  {meeting.title}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div style={styles.container}>
        <UserDashboardHeader
          title="Online Meeting Attendance"
          showBackButton={true}
          showHomeButton={false}
        />
        <div style={styles.scrollableContent}>
          <div style={styles.content}>
            <div style={styles.errorContainer}>
              <div style={{ fontSize: '48px', marginBottom: spacing.md }}>❌</div>
              <div style={{ ...styles.errorText, color: colors.error }}>
                Failed to connect to meeting
              </div>
              <div style={{ ...styles.errorSubtext, color: colors.textSecondary }}>
                {connectionError}
              </div>
              <button
                onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meetingId}/summary`)}
                style={{ ...styles.retryButton, backgroundColor: colors.primary }}
              >
                Back to Meeting Summary
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = getAttendanceStats();

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Online Meeting Attendance"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Meeting Info */}
          {meeting && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.meetingHeader}>
                <div style={{
                  ...styles.meetingTypeIcon,
                  backgroundColor: colors.secondary + '20'
                }}>
                  <span style={{ fontSize: '24px' }}>🎬</span>
                </div>
                <div style={styles.meetingInfo}>
                  <h2 style={{ ...styles.meetingTitle, color: colors.text }}>
                    {meeting.title}
                  </h2>
                  <p style={{ ...styles.meetingDate, color: colors.textSecondary }}>
                    {new Date(meeting.scheduledAt).toLocaleDateString()} at {new Date(meeting.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <p style={{ ...styles.meetingLocation, color: colors.textSecondary }}>
                    📍 Online Meeting
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Jitsi Meeting Interface */}
          {jitsiUrl && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <h3 style={{ ...styles.sectionTitleCard, color: colors.text }}>Meeting Room</h3>
              <div style={styles.jitsiContainer}>
                <iframe
                  src={jitsiUrl}
                  style={{
                    width: '100%',
                    height: '500px',
                    border: 'none',
                    borderRadius: borderRadius.md,
                  }}
                  allow="camera; microphone; fullscreen; display-capture"
                  title="Jitsi Meet"
                />
              </div>
              <div style={styles.meetingControls}>
                <button
                  onClick={() => window.open(jitsiUrl, '_blank')}
                  style={{ ...styles.controlButton, backgroundColor: colors.primary }}
                >
                  Open in New Tab
                </button>
              </div>
            </div>
          )}
          {/* Meeting Info */}
          {meeting && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <h2 style={{ ...styles.meetingTitleCard, color: colors.text }}>{meeting.title}</h2>
              <div style={styles.meetingMeta}>
                <div style={{ ...styles.metaItem, color: colors.textSecondary }}>
                  📅 {new Date(meeting.scheduledAt).toLocaleDateString()} at {new Date(meeting.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ ...styles.metaItem, color: colors.textSecondary }}>
                  🔗 {meeting.meetingUrl ? 'Meeting link available' : 'No meeting link'}
                </div>
                <div style={{ ...styles.metaItem, color: colors.textSecondary }}>
                  ⏱️ {meeting.duration} minutes
                </div>
                {meetingActive && (
                  <div style={{ ...styles.metaItem, color: colors.success, fontWeight: 'bold' }}>
                    🔴 Meeting is currently active
                  </div>
                )}
              </div>
              {meeting.meetingUrl && (
                <div style={styles.meetingLink}>
                  <a
                    href={meeting.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ ...styles.link, color: colors.primary }}
                  >
                    Join Meeting
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Attendance Stats */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitleCard, color: colors.text }}>Attendance Summary</h3>
            <div style={styles.statsGrid}>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.success }}>{stats.present}</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Present</div>
              </div>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.error }}>{stats.absent}</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Absent</div>
              </div>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.primary }}>{stats.total}</div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Total Members</div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitleCard, color: colors.text }}>Quick Actions</h3>
            <div style={styles.quickActions}>
              <button
                onClick={markAllPresent}
                style={{ ...styles.quickActionButton, backgroundColor: colors.success }}
              >
                Mark All Present
              </button>
              <button
                onClick={markAllAbsent}
                style={{ ...styles.quickActionButton, backgroundColor: colors.error }}
              >
                Mark All Absent
              </button>
            </div>
          </div>

          {/* Attendance List */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitleCard, color: colors.text }}>Mark Attendance</h3>
            <div style={styles.attendanceList}>
              {chamaMembers.map((member) => {
                const userId = member.user_id || member.id;
                const isPresent = attendance[userId] || false;

                return (
                  <div key={userId} style={styles.attendanceItem}>
                    <div style={styles.memberInfo}>
                      <div style={{ ...styles.memberName, color: colors.text }}>
                        {getMemberName(member)}
                      </div>
                      <div style={{ ...styles.memberRole, color: colors.textSecondary }}>
                        Member
                      </div>
                    </div>
                    <button
                      onClick={() => toggleAttendance(userId)}
                      style={{
                        ...styles.attendanceButton,
                        backgroundColor: isPresent ? colors.success : colors.error,
                      }}
                    >
                      <span style={{ fontSize: '18px', marginRight: '8px' }}>
                        {isPresent ? '✓' : '✗'}
                      </span>
                      {isPresent ? 'Present' : 'Absent'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button
              onClick={() => navigate(`/dashboard/chama/${chamaId}/meetings/${meetingId}/summary`)}
              style={{ ...styles.secondaryButton, border: `1px solid ${colors.border}`, color: colors.text }}
            >
              Cancel
            </button>
            <button
              onClick={saveAttendance}
              disabled={saving}
              style={{
                ...styles.primaryButton,
                backgroundColor: saving ? colors.textSecondary : colors.primary,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Attendance'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxxl,
  },
  loadingText: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.md,
  },
  loadingProgress: {
    width: '200px',
    height: '4px',
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: spacing.sm,
  },
  loadingProgressBar: {
    height: '100%',
    transition: 'width 0.3s ease',
  },
  loadingSubtext: {
    fontSize: typography.fontSize.sm,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxxl,
  },
  errorText: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  errorSubtext: {
    fontSize: typography.fontSize.base,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  retryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.md,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  meetingHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: spacing.md,
  },
  meetingTypeIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  meetingInfo: {
    flex: 1,
  },
  meetingTitleCard: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.xs,
  },
  meetingDate: {
    fontSize: typography.fontSize.base,
    margin: 0,
    marginBottom: spacing.xs / 2,
  },
  meetingLocation: {
    fontSize: typography.fontSize.sm,
    margin: 0,
  },
  sectionTitleCard: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.md,
  },
  jitsiContainer: {
    marginBottom: spacing.md,
  },
  meetingControls: {
    display: 'flex',
    justifyContent: 'center',
  },
  controlButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.md,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  card: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    ...getShadowStyle('sm'),
  },
  meetingTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.md,
  },
  meetingMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  metaItem: {
    fontSize: typography.fontSize.sm,
  },
  meetingLink: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
  },
  link: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    textDecoration: 'none',
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.md,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.md,
  },
  statItem: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.xs / 2,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
  },
  quickActions: {
    display: 'flex',
    gap: spacing.md,
  },
  quickActionButton: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  attendanceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  attendanceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.xs / 2,
  },
  memberRole: {
    fontSize: typography.fontSize.sm,
  },
  attendanceButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderRadius: borderRadius.md,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  actionButtons: {
    display: 'flex',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  primaryButton: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  secondaryButton: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    backgroundColor: 'transparent',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
};

export default OnlineMeetingAttendance;